import React, { useEffect, useState } from 'react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useNavigate } from 'react-router-dom';
import { getFileUploadHistory } from '../../services/fileTransferHistoryService';
import { markLastLogin } from '../../services/userService';
import appInsightsLogger from '../../utils/appInsights.js';

function Dashboard({ userDetails }) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [uploadsLoading, setUploadsLoading] = useState(false);
  const [account, setAccount] = useState({
    organization: '',
    contact: '',
    lastLogin: ''
  });
  const [uploads, setUploads] = useState([]);

  useEffect(() => {
    const loadDashboardData = async () => {
      appInsightsLogger.trackEvent('Dashboard_Load_Started', {
        component: 'Dashboard',
        userId: userDetails?.userId,
        organizationId: userDetails?.organizationId
      });

      setIsLoading(true);

      // Destructure userDetails
      const {
        organization = '',
        contact = '',
        lastLoginDate = null,
        passwordModifiedDate = null,
        userId = null,
        emailId = '',
        organizationId = null,
        firstName = '',
        lastName = ''
      } = userDetails || {};

      setAccount({
        organization,
        contact,
        emailId,
        firstName,
        lastName,
        lastLogin: lastLoginDate
          ? new Date(lastLoginDate).toLocaleDateString() + ' ' + new Date(lastLoginDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          : 'No last login.'
      });

      // Run markLastLogin and getFileUploadHistory in parallel
      const promises = [];

      if (userId && emailId) {
        const markLastLoginDto = {
          userId,
          email: emailId
        };
        promises.push(markLastLogin(markLastLoginDto));
      }

      if (organizationId) {
        setUploadsLoading(true);
        promises.push(
          getFileUploadHistory(organizationId, true)
            .then(data => {
              setUploads(data.slice(0, 5));
            })
            .catch(() => {
              setUploads([]);
            })
            .finally(() => {
              setUploadsLoading(false);
            })
        );
      } else {
        setUploads([]);
      }

      try {
        await Promise.all(promises);
        appInsightsLogger.trackEvent('Dashboard_Load_Success', {
          component: 'Dashboard',
          organizationId: organizationId
        });
      } catch (error) {
        appInsightsLogger.trackException(error, {
          component: 'Dashboard',
          organizationId: organizationId
        });
        console.error('Error loading dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (userDetails) {
      loadDashboardData();
    }
  }, [userDetails]);

  return (
    <div className="dashboard-main" style={{ maxWidth: 1200, margin: '32px auto', padding: '0 16px' }}>
      {isLoading ? (
        <div className="card-loading" style={{ minHeight: 400 }}>
          <LoadingSpinner size="large" />
        </div>
      ) : (
        <div className="dashboard-row" style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <div className="dashboard-card" style={{ background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', padding: '24px 20px', marginBottom: 24, flex: 1 }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 18 }}>Recent Uploads</h3>
            {uploadsLoading ? (
              <div className="table-loading">
                <LoadingSpinner size="medium" />
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ background: '#fafafa', fontWeight: 600, padding: '12px 10px', textAlign: 'left', borderBottom: '1px solid #eee' }}>File Name</th>
                    <th style={{ background: '#fafafa', fontWeight: 600, padding: '12px 10px', textAlign: 'left', borderBottom: '1px solid #eee' }}>Upload Date</th>
                    <th style={{ background: '#fafafa', fontWeight: 600, padding: '12px 10px', textAlign: 'left', borderBottom: '1px solid #eee' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {uploads.length === 0 ? (
                    <tr>
                      <td colSpan={3} style={{ textAlign: 'center', padding: '20px' }}>No uploads found.</td>
                    </tr>
                  ) : (
                    uploads.map((item, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: '12px 10px', borderBottom: '1px solid #eee' }}>{item.fileName}</td>
                        <td style={{ padding: '12px 10px', borderBottom: '1px solid #eee' }}>{item.createdDate ? new Date(item.createdDate).toLocaleDateString() + ' ' + new Date(item.createdDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</td>
                        <td style={{ padding: '12px 10px', borderBottom: '1px solid #eee' }}>
                          {item.status === 'success' || item.status === 'Success' ? (
                            <span style={{ color: '#228b22', fontWeight: 500 }}>✓ Success</span>
                          ) : (
                            <span style={{ color: '#c62828', fontWeight: 500 }}>✗ Failed</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
          <div className="dashboard-card" style={{ background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', padding: '24px 20px', marginBottom: 24, maxWidth: 350, minWidth: 340 }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 18 }}>Account Information</h3>
            <div style={{ marginBottom: 5, overflowWrap: 'break-word', wordBreak: 'break-word' }}><b>Organization:</b> {account.organization}</div>
            <div style={{ marginBottom: 5, overflowWrap: 'break-word', wordBreak: 'break-word' }}><b>Email Id:</b> {account.emailId || ''}</div>
            <div style={{ marginBottom: 5, overflowWrap: 'break-word', wordBreak: 'break-word' }}><b>Name:</b> {account.firstName || ''} {account.lastName || ''}</div>
            <div style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}><b>Last Login:</b> {account.lastLogin}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
